import { QK } from '@/js/consts/query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
    getProtocol,
    postAICoachingProtocol,
    postProtocol,
    postProtocolWithoutInteraction,
    getCoachableAgents,
    postProtocolCommentForSection,
    getProtocolTable,
    updateProtocolTableLayout,
    patchProtocol,
    postProtocolCommentForQuestionAnswer,
    postApproveProtocols,
    patchProtocolComment,
    deleteProtocolComment,
    updateProtocolApprovalStatus,
    deleteProtocols,
} from './protocolApi';

import {
    ISortOption,
    RequestPatchProtocol,
    RequestPostAICoachingProtocol,
    RequestPostProtocol,
    RequestPostProtocolApprovalStatus,
    RequestPostProtocolWithoutInteraction,
    RequestUpdateProtocolTableLayout,
} from './protocolModel';
import { computed, MaybeRefOrGetter, Ref, toValue, WritableComputedRef } from 'vue';
import { EchoFilterRequest } from '../filter/types';
import {
    InteractionTableStoreName,
    useInteractionTableStoreByName,
} from '@/js/stores/interactions-table';

export const useQueryGetProtocol = (id: string) => {
    return useQuery({
        queryKey: [QK.PROTOCOL.GET, id],
        queryFn: async () => getProtocol({ id }),
        enabled: !!id,
    });
};

export const useLazyQueryGetProtocol = () => {
    return useMutation({
        mutationKey: [QK.PROTOCOL.GET],
        mutationFn: async (id: string) => getProtocol({ id }),
    });
};

export const useGetProtocolTable = (
    page: WritableComputedRef<number>,
    pageSize: WritableComputedRef<number>,
    sortFieldId: WritableComputedRef<string | null>,
    sortOrder: WritableComputedRef<string | null>,
    filter: Ref<EchoFilterRequest>
) => {
    const sortOption = computed<ISortOption | undefined>(() => {
        const column = sortFieldId.value;
        if (!column) return;
        const dir = String(sortOrder.value).toLowerCase() === 'asc' ? 'Asc' : 'Desc';
        return { columnId: column, sortDirection: dir };
    });

    const queryKey = computed(() => [
        QK.PROTOCOL.TABLE,
        page.value,
        pageSize.value,
        sortOrder.value ?? '',
        sortFieldId.value ?? '',
        filter.value,
    ]);

    return useQuery({
        queryKey,
        queryFn: async () =>
            await getProtocolTable({
                page: page.value,
                pageSize: pageSize.value,
                filter: filter.value,
                sortOption: sortOption.value,
            }),
        select: (data) => {
            const { rowHeaders, ...interactionObject } = data;

            const rows = [...data.rowHeaders];

            const columnIdToNameMap: Record<string, string> = {};

            data.columnHeaders.forEach((col) => {
                if (col.translationKey === 'table-protocol-column-agent-has-read') {
                    columnIdToNameMap[col.id] = 'agent-has-read';
                } else if (col.translationKey === 'table-protocol-column-agent-has-commented') {
                    columnIdToNameMap[col.id] = 'agent-has-commented';
                } else {
                    columnIdToNameMap[col.id] = col.filterKey;
                }
            });

            data.rowCells.forEach((cell) => {
                rows[cell.rowIndex][columnIdToNameMap[cell.columnId]] = cell.value;
                rows[cell.rowIndex][cell.columnId] = cell.value;
            });

            return { ...interactionObject, rowCells: rows };
        },
    });
};

export const useMutationPostProtocol = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: RequestPostProtocol) => postProtocol(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.PROTOCOL.TABLE] });
        },
    });
};

export const useMutationPatchProtocol = (protocolId: Ref<string>) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: RequestPatchProtocol) => patchProtocol(protocolId.value, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.PROTOCOL.TABLE] });
            queryClient.invalidateQueries({ queryKey: [QK.PROTOCOL.GET, protocolId.value] });
        },
    });
};

export const useMutationPostAIProtocol = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: RequestPostAICoachingProtocol) => postAICoachingProtocol(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.PROTOCOL.TABLE] });
        },
    });
};

export const useMutationPostProtocolWithoutInteraction = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: RequestPostProtocolWithoutInteraction) =>
            postProtocolWithoutInteraction(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.PROTOCOL.TABLE] });
        },
    });
};

export const useQueryGetCoachableAgents = () => {
    return useQuery({
        queryKey: [QK.PROTOCOL.COACHABLE_AGENTS],
        queryFn: async () => getCoachableAgents(),
    });
};

export const useMutationPostApproveProtocols = (protocolIds: MaybeRefOrGetter<string[]>) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => postApproveProtocols(toValue(protocolIds)),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.PROTOCOL.TABLE] });

            for (const protocolId of toValue(protocolIds)) {
                queryClient.invalidateQueries({ queryKey: [QK.PROTOCOL.GET, protocolId] });
            }
        },
    });
};

export const useMutationPostProtocolCommentForSection = (protocolId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: { protocolSectionId: string; text: string }) =>
            postProtocolCommentForSection(protocolId, data.protocolSectionId, data.text),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.PROTOCOL.GET, protocolId] });
        },
    });
};

export const useMutationPostProtocolCommentForQuestion = (protocolId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: { protocolQuestionAnswerId: string; text: string }) =>
            postProtocolCommentForQuestionAnswer(
                protocolId,
                data.protocolQuestionAnswerId,
                data.text
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.PROTOCOL.GET, protocolId] });
        },
    });
};

export const useMutationPatchProtocolComment = (protocolId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: { commentId: string; text: string }) =>
            patchProtocolComment(protocolId, data.commentId, data.text),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.PROTOCOL.GET, protocolId] });
        },
    });
};

export const useMutationDeleteProtocolComment = (protocolId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (commentId: string) => deleteProtocolComment(protocolId, commentId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.PROTOCOL.GET, protocolId] });
        },
    });
};

export const useMutationUpdateProtocolTableLayout = () => {
    return useMutation({
        mutationFn: async (columns: RequestUpdateProtocolTableLayout['columns']) =>
            await updateProtocolTableLayout({ columns, tableKey: 'Protocols' }),
    });
};

export const useMutationUpdateProtocolApprovalStatus = () => {
    const queryClient = useQueryClient();
    const tableStore = useInteractionTableStoreByName(InteractionTableStoreName.ProtocolTable);
    return useMutation({
        mutationFn: async (data: RequestPostProtocolApprovalStatus) =>
            await updateProtocolApprovalStatus(data),

        onSuccess: async () => {
            tableStore.clearSelection();
            tableStore.setSelectionMode(false);

            await Promise.all([
                queryClient.invalidateQueries({ queryKey: [QK.PROTOCOL.TABLE] }),
                queryClient.invalidateQueries({ queryKey: [QK.PROTOCOL.GET] }),
            ]);
        },
    });
};

export const useMutationDeleteProtocols = () => {
    const queryClient = useQueryClient();
    const tableStore = useInteractionTableStoreByName(InteractionTableStoreName.ProtocolTable);
    return useMutation({
        mutationFn: deleteProtocols,
        onSuccess: async () => {
            tableStore.clearSelection();
            tableStore.setSelectionMode(false);

            await queryClient.invalidateQueries({
                queryKey: [QK.PROTOCOL.TABLE],
            });
        },
    });
};
